"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || (function () {
    var ownKeys = function(o) {
        ownKeys = Object.getOwnPropertyNames || function (o) {
            var ar = [];
            for (var k in o) if (Object.prototype.hasOwnProperty.call(o, k)) ar[ar.length] = k;
            return ar;
        };
        return ownKeys(o);
    };
    return function (mod) {
        if (mod && mod.__esModule) return mod;
        var result = {};
        if (mod != null) for (var k = ownKeys(mod), i = 0; i < k.length; i++) if (k[i] !== "default") __createBinding(result, mod, k[i]);
        __setModuleDefault(result, mod);
        return result;
    };
})();
Object.defineProperty(exports, "__esModule", { value: true });
exports.AdventurePreprocessor = void 0;
const fs = __importStar(require("fs"));
const path = __importStar(require("path"));
const preprocessor_1 = require("../types/preprocessor");
const SnippetExpander_1 = require("./SnippetExpander");
/**
 * AdventurePreprocessor handles loading and inlining sub-adventure files,
 * then expands <snippet:name> tags inside triple-quoted content.
 * Sub-adventures work like C++ includes where content is inlined before parsing.
 */
class AdventurePreprocessor {
    constructor(config) {
        this.basePath = config.basePath;
        this.config = config;
    }
    /**
     * Process main adventure text by loading and inlining sub-adventures, then expanding snippets.
     * @param mainAdventureText The main adventure text
     * @param currentAdventureId The ID of the current adventure (for error reporting)
     * @returns Processed adventure text with sub-adventures inlined and snippets expanded
     */
    process(mainAdventureText, currentAdventureId) {
        const warnings = [];
        const loadingStack = [currentAdventureId];
        const sourceMapping = [];
        try {
            let processedText = this.processText(mainAdventureText, loadingStack, warnings, currentAdventureId, sourceMapping);
            if (this.config.snippetsRoot || this.config.adventureDirectory) {
                processedText = (0, SnippetExpander_1.expandSnippetsInText)(processedText, this.config);
            }
            return {
                processedText,
                warnings,
                sourceMapping
            };
        }
        catch (error) {
            if (error instanceof preprocessor_1.PreprocessorError) {
                throw error;
            }
            throw new preprocessor_1.PreprocessorError(`Unexpected error during preprocessing: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
    /**
     * Recursively process adventure text and inline sub-adventures
     */
    processText(text, loadingStack, warnings, sourceFile, sourceMapping) {
        // Find and extract Sub-Adventures section
        const { subAdventureNames, processedMainText } = this.extractSubAdventuresSection(text);
        if (subAdventureNames.length === 0) {
            // Track source mapping for main text
            const mainTextLines = processedMainText.split('\n');
            for (let i = 0; i < mainTextLines.length; i++) {
                sourceMapping.push({ line: i + 1, sourceFile });
            }
            return processedMainText;
        }
        // Separate the main adventure into header (before first section) and sections (after first section)
        const { header, sections } = this.separateAdventureStructure(processedMainText);
        // Track source mapping for header
        const headerLines = header.split('\n');
        for (let i = 0; i < headerLines.length; i++) {
            sourceMapping.push({ line: i + 1, sourceFile });
        }
        // Merge each sub-adventure into the main sections independently so file-start
        // sections like "Variables:" are parsed with the same semantics regardless of
        // which section the previous sub-adventure ended with.
        let mergedSections = sections;
        const headerLineCount = headerLines.length;
        // mergeSections places newly merged sub-adventure content before existing merged sections.
        // Iterate in reverse so final output preserves the original sub-adventure declaration order.
        for (const subAdventureName of [...subAdventureNames].reverse()) {
            // Check for circular dependency
            if (loadingStack.includes(subAdventureName)) {
                throw new preprocessor_1.CircularDependencyError([...loadingStack, subAdventureName]);
            }
            // Load sub-adventure content
            const subAdventureContent = this.loadSubAdventure(subAdventureName, loadingStack, warnings);
            // Add to loading stack
            loadingStack.push(subAdventureName);
            // Recursively process sub-adventure's sub-adventures
            const processedSubAdventure = this.processText(subAdventureContent, loadingStack, warnings, subAdventureName, sourceMapping);
            // Remove the Sub-Adventures section from sub-adventure content
            const cleanedSubAdventure = this.removeSubAdventuresSection(processedSubAdventure);
            // Track source mapping for sub-adventure content
            const subAdventureLines = cleanedSubAdventure.split('\n');
            for (let i = 0; i < subAdventureLines.length; i++) {
                sourceMapping.push({ line: headerLineCount + sourceMapping.length + 1, sourceFile: subAdventureName });
            }
            mergedSections = this.mergeSections(cleanedSubAdventure, mergedSections);
            // Remove from loading stack
            loadingStack.pop();
        }
        // Track source mapping for sections
        const sectionsLines = sections.split('\n');
        for (let i = 0; i < sectionsLines.length; i++) {
            sourceMapping.push({ line: headerLineCount + 1 + i, sourceFile });
        }
        // Combine: main adventure header first, then merged sections (sub-adventures + main)
        return header + '\n\n' + mergedSections;
    }
    /**
     * Merge sections from sub-adventures into main adventure sections
     * This avoids creating duplicate section headers (e.g., multiple "## Scenes" sections)
     */
    mergeSections(subAdventureContent, mainSections) {
        if (!mainSections) {
            return subAdventureContent;
        }
        if (!subAdventureContent.trim()) {
            return mainSections;
        }
        // Parse both into section maps. Sub-adventure content may have a file (e.g. basic-training) that starts with Variables: after a blank; main content must not treat scene-level Variables: as adventure-level.
        const subSections = this.parseSections(subAdventureContent, { allowVariablesAfterBlankInScenes: true });
        const mainSectionsParsed = this.parseSections(mainSections, { allowVariablesAfterBlankInScenes: false });
        // Merge sections
        const merged = [];
        const allSectionNames = new Set([...subSections.keys(), ...mainSectionsParsed.keys()]);
        // Get section order from main adventure first, then add any extra sections from sub-adventures
        const sectionOrder = [];
        const mainSectionOrder = this.getSectionOrder(mainSections);
        for (const section of mainSectionOrder) {
            if (allSectionNames.has(section)) {
                sectionOrder.push(section);
            }
        }
        // Add any extra sections from sub-adventures
        for (const section of subSections.keys()) {
            if (!sectionOrder.includes(section)) {
                sectionOrder.push(section);
            }
        }
        // Build merged output
        for (const sectionName of sectionOrder) {
            const subContent = this.normalizeMergedSectionContent(sectionName, subSections.get(sectionName) || '');
            const mainContent = this.normalizeMergedSectionContent(sectionName, mainSectionsParsed.get(sectionName) || '');
            merged.push(`## ${sectionName}`);
            if (subContent) {
                merged.push(subContent);
            }
            if (mainContent) {
                if (subContent) {
                    // Add spacing between sub and main content
                    merged.push('');
                }
                merged.push(mainContent);
            }
        }
        return merged.join('\n');
    }
    normalizeMergedSectionContent(sectionName, content) {
        if (sectionName !== 'Variables') {
            return content;
        }
        return content
            .split('\n')
            .filter((line) => line.trim() !== 'Variables:')
            .join('\n')
            .replace(/^\n+/, '')
            .replace(/\n+$/, '');
    }
    /**
     * Parse text into sections (## SectionName -> content)
     * Merges content when the same section appears multiple times
     * @param options.allowVariablesAfterBlankInScenes - when true, "Variables:" after a blank line inside Scenes starts an adventure-level Variables section (for concatenated sub-adventure content). When false, only file-start "Variables:" does (avoids scene-level Variables in main adventure).
     */
    parseSections(text, options) {
        const allowVariablesAfterBlankInScenes = options?.allowVariablesAfterBlankInScenes ?? false;
        const sections = new Map();
        const lines = text.split('\n');
        let currentSection = null;
        let currentContent = [];
        let previousLineWasBlank = false;
        // Valid adventure section names (capitalized)
        const validSectionNames = new Set([
            'Sub-Adventures', 'Sub-adventures', 'sub-adventures',
            'Scripts', 'scripts',
            'Character Variables', 'Character variables', 'character variables',
            'Shared Variables', 'Shared variables', 'shared variables',
            'Players', 'Characters', 'Items', 'Locations', 'Scenes',
            'Stats', 'Variables',
            'Activity Types', 'Activity types', 'activity types',
            'Activities', 'activities',
            'Entity Types', 'Entity types', 'entity types'
        ]);
        // Variable declaration pattern: optional indent, type, name, "desc", = value
        const variableDeclarationPattern = /^\s*\w+\s+\w+:\s*"[^"]*"\s*=\s*.+/;
        for (const line of lines) {
            const trimmed = line.trim();
            const canStartVariables = currentSection === null || (allowVariablesAfterBlankInScenes && currentSection === 'Scenes' && previousLineWasBlank);
            // Capture "Variables:" as adventure-level section at file start, or (when parsing sub-adventure content) after a blank line in Scenes
            if (trimmed === 'Variables:' && canStartVariables) {
                if (currentSection) {
                    const existingContent = sections.get(currentSection) || '';
                    const newContent = currentContent.join('\n');
                    if (existingContent && newContent) {
                        sections.set(currentSection, existingContent + '\n\n' + newContent);
                    }
                    else {
                        sections.set(currentSection, existingContent + newContent);
                    }
                }
                currentSection = 'Variables';
                currentContent = [line];
                previousLineWasBlank = false;
                continue;
            }
            // When in Variables section, only collect variable declaration lines (or blank); otherwise close Variables and process line
            if (currentSection === 'Variables' && trimmed !== '' && !variableDeclarationPattern.test(line)) {
                const existingContent = sections.get('Variables') || '';
                const newContent = currentContent.join('\n');
                sections.set('Variables', existingContent && newContent ? existingContent + '\n\n' + newContent : existingContent + newContent);
                currentSection = null;
                currentContent = [];
            }
            // Only treat lines starting with exactly "## " as section headers if they're valid section names
            if (trimmed.startsWith('## ')) {
                // Extract section name after "##"
                const sectionName = trimmed.substring(3).trim();
                // Check if this is a valid adventure section (case-insensitive comparison for standard sections)
                const normalizedName = sectionName.toLowerCase();
                const isValidSection = validSectionNames.has(sectionName) ||
                    validSectionNames.has(sectionName.substring(0, 1).toUpperCase() + sectionName.substring(1));
                if (isValidSection) {
                    // Save previous section (merge if it already exists)
                    if (currentSection) {
                        const existingContent = sections.get(currentSection) || '';
                        const newContent = currentContent.join('\n');
                        // Merge: add existing content first, then new content with spacing
                        if (existingContent && newContent) {
                            sections.set(currentSection, existingContent + '\n\n' + newContent);
                        }
                        else {
                            sections.set(currentSection, existingContent + newContent);
                        }
                    }
                    // Start new section
                    currentSection = sectionName;
                    currentContent = [];
                }
                else {
                    // Not a valid section header, treat as content
                    if (currentSection) {
                        currentContent.push(line);
                    }
                }
            }
            else if (currentSection) {
                currentContent.push(line);
            }
            previousLineWasBlank = trimmed === '';
        }
        // Save last section (merge if it already exists)
        if (currentSection) {
            const existingContent = sections.get(currentSection) || '';
            const newContent = currentContent.join('\n');
            if (existingContent && newContent) {
                sections.set(currentSection, existingContent + '\n\n' + newContent);
            }
            else {
                sections.set(currentSection, existingContent + newContent);
            }
        }
        return sections;
    }
    /**
     * Get section order from text
     */
    getSectionOrder(text) {
        const lines = text.split('\n');
        const order = [];
        for (const line of lines) {
            const trimmed = line.trim();
            if (trimmed.startsWith('##')) {
                const sectionName = trimmed.substring(2).trim();
                if (sectionName && !order.includes(sectionName)) {
                    order.push(sectionName);
                }
            }
        }
        return order;
    }
    /**
     * Separate adventure into header (title, description, etc.) and sections
     */
    separateAdventureStructure(text) {
        const lines = text.split('\n');
        let sectionStart = -1;
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i].trim();
            // Look for the first section (## Characters, ## Players, etc.)
            if (line.startsWith('##') && line.length > 2) {
                sectionStart = i;
                break;
            }
        }
        if (sectionStart === -1) {
            // No sections found, return entire text as header
            return { header: text, sections: '' };
        }
        const header = lines.slice(0, sectionStart).join('\n');
        const sections = lines.slice(sectionStart).join('\n');
        return { header, sections };
    }
    /**
     * Extract Sub-Adventures section and return list of sub-adventure names
     */
    extractSubAdventuresSection(text) {
        const lines = text.split('\n');
        const subAdventures = [];
        const outputLines = [];
        let inSubAdventuresSection = false;
        let foundSection = false;
        let sectionStartLine = -1;
        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];
            const trimmed = line.trim();
            // Look for Sub-Adventures section header
            if (trimmed === '## Sub-Adventures' || trimmed === '## sub-adventures') {
                inSubAdventuresSection = true;
                foundSection = true;
                sectionStartLine = i;
                continue;
            }
            // If we're in the Sub-Adventures section, look for bullet points
            if (inSubAdventuresSection) {
                // Check if we've hit another section (## heading)
                if (trimmed.startsWith('##')) {
                    inSubAdventuresSection = false;
                    outputLines.push(line);
                    continue;
                }
                // Look for bullet points (- name)
                if (trimmed.startsWith('- ')) {
                    const subAdventureName = trimmed.substring(2).trim();
                    if (subAdventureName) {
                        subAdventures.push(subAdventureName);
                    }
                    continue; // Don't add this line to output
                }
                // If the line is not empty and not a bullet point, we've left the section
                if (trimmed !== '') {
                    inSubAdventuresSection = false;
                    outputLines.push(line);
                    continue;
                }
            }
            else {
                // Not in Sub-Adventures section, keep the line
                outputLines.push(line);
            }
        }
        const processedMainText = outputLines.join('\n');
        return {
            subAdventureNames: subAdventures,
            processedMainText
        };
    }
    /**
     * Remove Sub-Adventures section from text (for sub-adventure files)
     */
    removeSubAdventuresSection(text) {
        const lines = text.split('\n');
        const outputLines = [];
        let inSubAdventuresSection = false;
        for (const line of lines) {
            const trimmed = line.trim();
            // Look for Sub-Adventures section header
            if (trimmed === '## Sub-Adventures' || trimmed === '## sub-adventures') {
                inSubAdventuresSection = true;
                continue;
            }
            // If we're in the Sub-Adventures section, skip lines
            if (inSubAdventuresSection) {
                // Check if we've hit another section (## heading)
                if (trimmed.startsWith('##')) {
                    inSubAdventuresSection = false;
                    outputLines.push(line);
                    continue;
                }
                // Skip all lines until we hit a section or next line after empty line
                continue;
            }
            outputLines.push(line);
        }
        return outputLines.join('\n');
    }
    /**
     * Load sub-adventure file from filesystem
     */
    loadSubAdventure(subAdventureName, loadingStack, warnings) {
        // Try to resolve the file path
        const filePath = this.resolveSubAdventurePath(subAdventureName);
        if (!filePath) {
            throw new preprocessor_1.FileNotFoundError(subAdventureName, this.basePath);
        }
        try {
            const content = fs.readFileSync(filePath, 'utf-8');
            return content;
        }
        catch (error) {
            if (error instanceof Error && error.message.includes('ENOENT')) {
                throw new preprocessor_1.FileNotFoundError(subAdventureName, this.basePath);
            }
            throw new preprocessor_1.PreprocessorError(`Error reading sub-adventure file '${subAdventureName}': ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }
    /**
     * Resolve sub-adventure file path
     * Tries multiple locations:
     * 1. Exact filename in base path
     * 2. Add .sad extension if not present
     * 3. Check in subdirectories (e.g., ui/character-page -> ui/character-page.sad)
     */
    resolveSubAdventurePath(subAdventureName) {
        // Simple resolution: look for the file in basePath
        // If no extension, add .sad
        const fileName = subAdventureName.endsWith('.sad')
            ? subAdventureName
            : subAdventureName + '.sad';
        const filePath = path.join(this.basePath, fileName);
        if (fs.existsSync(filePath) && fs.statSync(filePath).isFile()) {
            return filePath;
        }
        return null;
    }
}
exports.AdventurePreprocessor = AdventurePreprocessor;
//# sourceMappingURL=AdventurePreprocessor.js.map